/**
 * Knowledge Control detail — Phase 11 admin/governance.
 */

import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  api,
  KnowledgeControlAction,
  KnowledgeControlDetail,
  KnowledgeControlHistoryEntry,
  KnowledgeValidationResponse,
} from '../lib/api'
import {
  KnowledgeControlAuthError,
  loadKnowledgeAdminToken,
  resolveKnowledgeAdminAccess,
} from '../lib/knowledgeControlAuth'
import { LoadingSpinner } from '../components/design-system/Loading'
import { Badge } from '../components/design-system/Badge'
import { Button } from '../components/design-system/Button'
import { Modal } from '../components/design-system/Modal'
import { EmptyState } from '../components/design-system/EmptyState'
import { useLocale } from '../contexts/LocaleContext'
import { actionLabel, formatLabel, lifecycleLabel, severityLabel, validationLabel } from '../lib/i18n'

const CONFIRM_ACTIONS = new Set<KnowledgeControlAction>([
  'approve',
  'reject',
  'revise',
  'activate',
  'deprecate',
])

export function KnowledgeControlDetailPage() {
  const { t, locale } = useLocale()
  const { ruleId } = useParams<{ ruleId: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [unauthorized, setUnauthorized] = useState(false)
  const [access, setAccess] = useState<'pending' | 'yes' | 'no'>(() => (loadKnowledgeAdminToken() ? 'yes' : 'pending'))
  const [rule, setRule] = useState<KnowledgeControlDetail | null>(null)
  const [history, setHistory] = useState<KnowledgeControlHistoryEntry[]>([])
  const [validation, setValidation] = useState<KnowledgeValidationResponse | null>(null)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [pendingAction, setPendingAction] = useState<KnowledgeControlAction | null>(null)
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (access !== 'pending') return
    let cancelled = false
    void resolveKnowledgeAdminAccess(() => api.me()).then((ok) => {
      if (!cancelled) {
        setAccess(ok ? 'yes' : 'no')
        if (!ok) setUnauthorized(true)
      }
    })
    return () => {
      cancelled = true
    }
  }, [access])

  useEffect(() => {
    if (!ruleId || access !== 'yes') {
      if (access === 'no') setLoading(false)
      return
    }
    load()
  }, [ruleId, access])

  async function load() {
    if (!ruleId) return
    try {
      setLoading(true)
      setError(null)
      const [detail, hist] = await Promise.all([
        api.getKnowledgeControlRule(ruleId),
        api.getKnowledgeControlHistory(ruleId),
      ])
      setRule(detail)
      setHistory(hist.entries)
    } catch (err) {
      if (err instanceof KnowledgeControlAuthError) {
        setUnauthorized(true)
      } else {
        setError(err instanceof Error ? err.message : t('kc.failed'))
      }
    } finally {
      setLoading(false)
    }
  }

  async function runAction(action: KnowledgeControlAction, confirmedReason: string) {
    if (!ruleId || !rule) return
    try {
      setBusy(true)
      setActionError(null)
      if (action === 'validate') {
        const result = await api.validateKnowledgeControlRule(ruleId)
        setValidation(result)
      } else if (action === 'submit_testing') {
        await api.transitionKnowledgeControlRule(ruleId, {
          to_status: 'testing',
          reason: confirmedReason,
          from_status: rule.status,
          version_number: rule.version_number,
        })
      } else if (action === 'deprecate') {
        await api.transitionKnowledgeControlRule(ruleId, {
          to_status: 'deprecated',
          reason: confirmedReason,
          from_status: rule.status,
          version_number: rule.version_number,
        })
      } else {
        await api.approveKnowledgeControlRule(ruleId, {
          action,
          notes: confirmedReason,
          revision_requested: action === 'revise' ? confirmedReason : undefined,
        })
      }
      setPendingAction(null)
      setReason('')
      await load()
    } catch (err) {
      if (err instanceof KnowledgeControlAuthError) {
        setUnauthorized(true)
      } else {
        setActionError(err instanceof Error ? err.message : t('kc.actionFailed'))
      }
    } finally {
      setBusy(false)
    }
  }

  function requestAction(action: KnowledgeControlAction) {
    if (action === 'validate') {
      void runAction(action, 'validate')
      return
    }
    if (CONFIRM_ACTIONS.has(action) || action === 'submit_testing') {
      setPendingAction(action)
      setReason('')
      return
    }
  }

  if (unauthorized) {
    return (
      <div style={{ padding: '32px' }} data-testid="knowledge-control-unauthorized">
        <EmptyState
          title={t('kc.authRequired')}
          description={t('kc.tokenLead')}
        />
        <Button onClick={() => navigate('/knowledge-control')} variant="primary">
          {t('nav.knowledgeControl')}
        </Button>
      </div>
    )
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', minHeight: '400px', alignItems: 'center' }}>
        <LoadingSpinner size="lg" text={t('kc.loading')} />
      </div>
    )
  }

  if (error || !rule) {
    return (
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '32px' }}>
        <h2 style={{ fontFamily: 'var(--reviewer-font-mono)', color: 'var(--reviewer-danger)' }}>
          {error || t('kc.notFound')}
        </h2>
        <Button onClick={() => navigate('/knowledge-control')} variant="secondary">
          {t('kc.empty')}
        </Button>
      </div>
    )
  }

  return (
    <div style={{ padding: '24px' }} data-testid="knowledge-control-detail">
      <div style={{ marginBottom: '16px', fontFamily: 'var(--reviewer-font-mono)', fontSize: '12px' }}>
        <Link to="/knowledge-control" style={{ color: 'var(--reviewer-primary)', textDecoration: 'none' }}>
          {t('nav.knowledgeControl')}
        </Link>
        <span> / {rule.rule_id}</span>
      </div>

      <h1 style={{ fontFamily: 'var(--reviewer-font-mono)', marginTop: 0 }}>{rule.title}</h1>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <Badge variant="warning">{lifecycleLabel(locale, rule.status)}</Badge>
        <Badge variant={rule.validation_status === 'pass' ? 'success' : 'default'}>
          {t('packs.colValidation')} {validationLabel(locale, rule.validation_status)}
        </Badge>
        <Badge variant="default">{severityLabel(locale, rule.severity)}</Badge>
        <Badge variant="default">{formatLabel(locale, rule.format_type)}</Badge>
        {rule.proposal_source && <Badge variant="info">{rule.proposal_source}</Badge>}
      </div>

      <p style={{ fontFamily: 'var(--reviewer-font-mono)', maxWidth: '720px' }}>{rule.description}</p>
      {rule.proposal_rationale && (
        <p style={{ fontFamily: 'var(--reviewer-font-mono)', color: 'var(--reviewer-text-secondary)' }}>
          {t('kc.rationale')}: {rule.proposal_rationale}
        </p>
      )}

      {actionError && (
        <div
          style={{
            border: '1px solid var(--reviewer-danger-border)',
            padding: '12px',
            marginBottom: '16px',
            fontFamily: 'var(--reviewer-font-mono)',
            color: 'var(--reviewer-danger)',
          }}
        >
          {actionError}
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '24px' }}>
        {rule.legal_actions.map((action) => (
          <Button
            key={action}
            variant={action === 'reject' || action === 'deprecate' ? 'danger' : 'primary'}
            size="sm"
            disabled={busy}
            onClick={() => requestAction(action)}
            data-testid={`knowledge-control-action-${action}`}
          >
            {actionLabel(locale, action)}
          </Button>
        ))}
        <Button variant="secondary" size="sm" onClick={() => navigate('/knowledge-control')}>
          {t('common.back')}
        </Button>
      </div>

      {validation && (
        <section style={{ marginBottom: '24px' }}>
          <h2 style={{ fontFamily: 'var(--reviewer-font-mono)', fontSize: '16px' }}>{t('packs.colValidation')}</h2>
          <p style={{ fontFamily: 'var(--reviewer-font-mono)' }}>
            {validationLabel(locale, validation.overall_status)} — {t('act.activate')}: {String(validation.can_activate)}
          </p>
          <ul>
            {validation.results.map((result) => (
              <li key={result.validation_type} style={{ fontFamily: 'var(--reviewer-font-mono)' }}>
                {result.validation_type}: {validationLabel(locale, result.status)}
                {result.error_message ? ` (${result.error_message})` : ''}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section style={{ marginBottom: '24px' }}>
        <h2 style={{ fontFamily: 'var(--reviewer-font-mono)', fontSize: '16px' }}>{t('kc.matchSpec')}</h2>
        <pre
          style={{
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: '12px',
            overflowX: 'auto',
            border: '1px solid var(--reviewer-border)',
            padding: '12px',
          }}
        >
          {JSON.stringify(rule.match_spec, null, 2)}
        </pre>
      </section>

      <section data-testid="knowledge-control-history">
        <h2 style={{ fontFamily: 'var(--reviewer-font-mono)', fontSize: '16px' }}>{t('packs.history')}</h2>
        {history.length === 0 ? (
          <p style={{ fontFamily: 'var(--reviewer-font-mono)', color: 'var(--reviewer-text-secondary)' }}>
            {t('packs.noHistory')}
          </p>
        ) : (
          <ol style={{ fontFamily: 'var(--reviewer-font-mono)', paddingLeft: '20px' }}>
            {history.map((entry) => (
              <li key={entry.id} style={{ marginBottom: '8px' }}>
                {lifecycleLabel(locale, entry.from_status || 'draft')} → {lifecycleLabel(locale, entry.to_status)} {entry.actor_name || '—'}
                {entry.actor_role ? ` (${entry.actor_role})` : ''}: {entry.reason}
              </li>
            ))}
          </ol>
        )}
      </section>

      <Modal
        isOpen={pendingAction !== null}
        onClose={() => setPendingAction(null)}
        title={pendingAction ? actionLabel(locale, pendingAction) : t('act.confirm')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setPendingAction(null)} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button
              variant={pendingAction === 'reject' || pendingAction === 'deprecate' ? 'danger' : 'primary'}
              disabled={busy || reason.trim().length < 1}
              onClick={() => pendingAction && void runAction(pendingAction, reason.trim())}
            >
              {t('act.confirm')}
            </Button>
          </>
        }
      >
        <p style={{ fontFamily: 'var(--reviewer-font-mono)', fontSize: '14px' }}>
          {t('packs.confirmBody')}
        </p>
        <textarea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          data-testid="knowledge-control-reason"
          rows={4}
          style={{
            width: '100%',
            marginTop: '12px',
            padding: '8px',
            border: '1px solid var(--reviewer-border)',
            borderRadius: 0,
            fontFamily: 'var(--reviewer-font-mono)',
            background: 'var(--reviewer-bg-primary)',
            color: 'var(--reviewer-text)',
          }}
        />
      </Modal>
    </div>
  )
}
